import {cn} from '@/lib/utils'

type PulseDotTone = 'success' | 'warning' | 'danger' | 'info' | 'muted' | 'primary'
type PulseDotSize = 'sm' | 'md'

interface PulseDotProps {
    tone?: PulseDotTone
    /**
     * `md` (default) = 0.5rem dot (h-2 w-2).
     * `sm` = 0.625rem dot (h-2.5 w-2.5) — used for chart legends.
     */
    size?: PulseDotSize
    /**
     * When true, renders a single solid dot without the pulsing animation.
     * Used for static legend indicators (chart keys, color swatches).
     */
    staticDot?: boolean
    className?: string
    'data-testid'?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const toneBgClasses: Record<PulseDotTone, string> = {
    success: 'bg-status-success',
    warning: 'bg-status-warning',
    danger: 'bg-status-danger',
    info: 'bg-status-info',
    muted: 'bg-muted',
    primary: 'bg-primary',
}
const sizeClasses: Record<PulseDotSize, string> = {
    sm: 'h-2 w-2',
    md: 'h-2.5 w-2.5',
}
/* eslint-enable i18next/no-literal-string */

/**
 * Colored dot used to indicate a status or serve as a chart legend marker.
 * Default form is a two-layer pulsing animation (used for live / SSE status).
 * Pass `staticDot` for a non-animated solid dot (used for chart legends).
 */
export function PulseDot({tone = 'success', size = 'sm', staticDot, className, 'data-testid': testId}: PulseDotProps) {
    const bg = toneBgClasses[tone]
    const dim = sizeClasses[size]
    if (staticDot) {
        return (
            <span
                className={cn('inline-block rounded-full', dim, bg, className)}
                data-testid={testId}
            />
        )
    }
    return (
        <span
            className={cn('relative flex', dim, className)}
            data-testid={testId}
        >
            <span className={cn('animate-ping absolute inset-0 rounded-full opacity-75', bg)}/>
            <span className={cn('relative inline-flex rounded-full', dim, bg)}/>
        </span>
    )
}
